import React from 'react';
import {useWpContext} from "../context/WordPressProvider.tsx";
import {useFetchCharacteristicsQuery} from "../../store/api-rest.ts";
import {CtrCaracteristiques, Maybe} from "../../graphql/generated/graphql-main.tsx";
import useCtrTypeCarac from "../../hooks/useCtrTypeCarac.ts";
import useCtrSousTypeCarac from "../../hooks/useCtrSousTypeCarac.ts";
import {findBy, groupBy} from "../../utils/array-utils.ts";
import useCtrMetaFields from "../../hooks/useCtrMetaFields.tsx";

/**
 * To make a fully responsive auto-flow column layout where content flows into X number of columns, the simplest
 * approach is to use 'column-count' (basic CSS) and break-inside: avoid, and it works perfectly for all formats.
 * @source
 * - https://tailwindcss.com/docs/columns#adding-based-on-column-count
 * - https://stackoverflow.com/questions/7785374/how-to-prevent-column-break-within-an-element#answer-7785711
 * @constructor
 */
const ListingTabCharacteristics = () => {
    const {others, is_fr} = useWpContext<ZListingSingle>();
    const {$entries: types} = useCtrTypeCarac();
    const {$entries: subTypes} = useCtrSousTypeCarac();
    const {toValue} = useCtrMetaFields();
    const {data} = useFetchCharacteristicsQuery(others.mls_id);

    // group sub-characteristics by main characteristic code
    const groups = data?.length ? groupBy(data, "TCAR_CODE") : {};
    const attr = (prop?: Maybe<keyof CtrCaracteristiques | string>) => {
        if (prop == null) return "nd";
        const type = findBy(types, "CODE", prop);
        return is_fr ? type?.DESCRIPTION_FRANCAISE : type?.DESCRIPTION_ANGLAISE;
    }
    const subAttr = (main: string, sub?: Maybe<keyof CtrCaracteristiques | string>) => {
        if (sub == null) return "nd";
        const _subTypes = subTypes.filter(it => it.TCAR_CODE == main);
        const type = findBy(_subTypes, "CODE", sub);
        return is_fr ? type?.DESCRIPTION_FRANCAISE : type?.DESCRIPTION_ANGLAISE;
    }
    const pwrap = (txt?: Maybe<string>) => txt?.length ? `(${txt})` : "";

    // dl = definition list
    return <section className={"columns-xs"}>
        {/* list entries grouped by main characteristic code, then list sub-characteristics */}
        {Object.entries(groups).map(([code, subs], i1) => {
            return <dl
                key={`char-group-${code}.${i1}`}
                className={"mb-4 break-inside-avoid"}
            >
                {/* name of the main characteristic -- dt = definition term */}
                <dt className={"font-bold"}>
                    {attr(code)}
                </dt>

                {/* list sub-characteristics */}
                {subs.map((it: CtrCaracteristiques, i2) => {
                    const val = toValue<CtrCaracteristiques>(it);
                    return <dd key={`char-${it.SCARAC_CODE}-${i2}`}>
                        {/* subtype of characteristic w/ number of the item (if applicable) */}
                        {subAttr(code, it?.SCARAC_CODE)} {' '}
                        {/* the number of instances of the said sub-characteristic */}
                        {pwrap(it?.NOMBRE)} {' '}
                        {/* amount of money spent on that sub-characteristic (e.g. see 11447707 Chauffe-eau) */}
                        {val("MONTANT", {type: "currency"})} {' '}
                        {/* any other relevant info */}
                        {pwrap(is_fr ? it?.INFORMATIONS_FRANCAISES : it?.INFORMATIONS_ANGLAISES)}
                    </dd>
                })}
            </dl>
        })}
    </section>;
};

export default ListingTabCharacteristics;